import { createClient } from '@supabase/supabase-js'

// ローカル開発環境のSupabase設定
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'http://127.0.0.1:54321'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0'

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10, // リアルタイム更新頻度制限
    },
  },
})

// データベース型定義（後でSupabase CLIで自動生成予定）
export type Database = {
  public: {
    Tables: {
      organizations: {
        Row: {
          id: string
          name: string
          address: string | null
          postal_code: string | null
          total_units: number | null
          fiscal_year_start_month: number
          management_company: string | null
          established_date: string | null
          contact_email: string | null
          contact_phone: string | null
          settings: Record<string, any>
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          address?: string | null
          postal_code?: string | null
          total_units?: number | null
          fiscal_year_start_month?: number
          management_company?: string | null
          established_date?: string | null
          contact_email?: string | null
          contact_phone?: string | null
          settings?: Record<string, any>
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          address?: string | null
          postal_code?: string | null
          total_units?: number | null
          fiscal_year_start_month?: number
          management_company?: string | null
          established_date?: string | null
          contact_email?: string | null
          contact_phone?: string | null
          settings?: Record<string, any>
          created_at?: string
          updated_at?: string
        }
      }
      user_profiles: {
        Row: {
          id: string
          display_name: string
          role: 'admin' | 'member' | 'viewer'
          organization_id: string
          current_organization_id: string | null
          avatar_url: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          display_name: string
          role?: 'admin' | 'member' | 'viewer'
          organization_id: string
          current_organization_id?: string | null
          avatar_url?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          display_name?: string
          role?: 'admin' | 'member' | 'viewer'
          organization_id?: string
          current_organization_id?: string | null
          avatar_url?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      user_organizations: {
        Row: {
          id: string
          user_id: string
          organization_id: string
          role: 'admin' | 'member' | 'viewer'
          joined_at: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          organization_id: string
          role?: 'admin' | 'member' | 'viewer'
          joined_at?: string
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          organization_id?: string
          role?: 'admin' | 'member' | 'viewer'
          joined_at?: string
          created_at?: string
        }
      }
      invitation_tokens: {
        Row: {
          id: string
          organization_id: string
          token: string
          created_by: string | null
          expires_at: string
          used_at: string | null
          used_by: string | null
          created_at: string
        }
        Insert: {
          id?: string
          organization_id: string
          token: string
          created_by?: string | null
          expires_at: string
          used_at?: string | null
          used_by?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          organization_id?: string
          token?: string
          created_by?: string | null
          expires_at?: string
          used_at?: string | null
          used_by?: string | null
          created_at?: string
        }
      }
      account_balances: {
        Row: {
          id: string
          organization_id: string
          account_type: 'general' | 'reserve'
          balance: number
          last_updated: string
          updated_by: string
        }
        Insert: {
          id?: string
          organization_id: string
          account_type: 'general' | 'reserve'
          balance?: number
          last_updated?: string
          updated_by: string
        }
        Update: {
          id?: string
          organization_id?: string
          account_type?: 'general' | 'reserve'
          balance?: number
          last_updated?: string
          updated_by?: string
        }
      }
      transactions: {
        Row: {
          id: string
          organization_id: string
          account_type: 'general' | 'reserve'
          type: 'income' | 'expense'
          amount: number
          category: string
          subcategory: string | null
          description: string | null
          reference_number: string | null
          transaction_date: string
          year: number
          month: number
          created_by: string
          approved_by: string | null
          approved_at: string | null
          receipt_file_path: string | null
          import_batch_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          organization_id: string
          account_type: 'general' | 'reserve'
          type: 'income' | 'expense'
          amount: number
          category: string
          subcategory?: string | null
          description?: string | null
          reference_number?: string | null
          transaction_date: string
          year: number
          month: number
          created_by: string
          approved_by?: string | null
          approved_at?: string | null
          receipt_file_path?: string | null
          import_batch_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          organization_id?: string
          account_type?: 'general' | 'reserve'
          type?: 'income' | 'expense'
          amount?: number
          category?: string
          subcategory?: string | null
          description?: string | null
          reference_number?: string | null
          transaction_date?: string
          year?: number
          month?: number
          created_by?: string
          approved_by?: string | null
          approved_at?: string | null
          receipt_file_path?: string | null
          import_batch_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      categories: {
        Row: {
          id: string
          organization_id: string
          name: string
          account_type: 'general' | 'reserve'
          type: 'income' | 'expense'
          parent_category_id: string | null
          color_code: string
          sort_order: number
          is_active: boolean
          created_at: string
        }
        Insert: {
          id?: string
          organization_id: string
          name: string
          account_type: 'general' | 'reserve'
          type: 'income' | 'expense'
          parent_category_id?: string | null
          color_code?: string
          sort_order?: number
          is_active?: boolean
          created_at?: string
        }
        Update: {
          id?: string
          organization_id?: string
          name?: string
          account_type?: 'general' | 'reserve'
          type?: 'income' | 'expense'
          parent_category_id?: string | null
          color_code?: string
          sort_order?: number
          is_active?: boolean
          created_at?: string
        }
      }
      simulations: {
        Row: {
          id: string
          organization_id: string
          name: string
          current_reserve_balance: number
          monthly_contribution: number
          maintenance_year: number
          maintenance_cost: number
          annual_increase_rate: number
          simulation_years: number
          results: Record<string, any> | null
          created_by: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          organization_id: string
          name?: string
          current_reserve_balance: number
          monthly_contribution: number
          maintenance_year: number
          maintenance_cost: number
          annual_increase_rate?: number
          simulation_years?: number
          results?: Record<string, any> | null
          created_by: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          organization_id?: string
          name?: string
          current_reserve_balance?: number
          monthly_contribution?: number
          maintenance_year?: number
          maintenance_cost?: number
          annual_increase_rate?: number
          simulation_years?: number
          results?: Record<string, any> | null
          created_by?: string
          created_at?: string
          updated_at?: string
        }
      }
      import_histories: {
        Row: {
          id: string
          organization_id: string
          file_name: string
          file_path: string | null
          file_size: number | null
          mime_type: string | null
          account_type: 'general' | 'reserve'
          import_date: string
          status: 'success' | 'error' | 'warning'
          record_count: number
          error_details: Record<string, any> | null
          imported_by: string
          created_at: string
        }
        Insert: {
          id?: string
          organization_id: string
          file_name: string
          file_path?: string | null
          file_size?: number | null
          mime_type?: string | null
          account_type: 'general' | 'reserve'
          import_date?: string
          status: 'success' | 'error' | 'warning'
          record_count?: number
          error_details?: Record<string, any> | null
          imported_by: string
          created_at?: string
        }
        Update: {
          id?: string
          organization_id?: string
          file_name?: string
          file_path?: string | null
          file_size?: number | null
          mime_type?: string | null
          account_type?: 'general' | 'reserve'
          import_date?: string
          status?: 'success' | 'error' | 'warning'
          record_count?: number
          error_details?: Record<string, any> | null
          imported_by?: string
          created_at?: string
        }
      }
      export_histories: {
        Row: {
          id: string
          organization_id: string
          export_type: string
          file_name: string
          file_path: string | null
          date_range_start: string | null
          date_range_end: string | null
          account_type: 'general' | 'reserve' | null
          record_count: number
          exported_by: string
          created_at: string
        }
        Insert: {
          id?: string
          organization_id: string
          export_type: string
          file_name: string
          file_path?: string | null
          date_range_start?: string | null
          date_range_end?: string | null
          account_type?: 'general' | 'reserve' | null
          record_count?: number
          exported_by: string
          created_at?: string
        }
        Update: {
          id?: string
          organization_id?: string
          export_type?: string
          file_name?: string
          file_path?: string | null
          date_range_start?: string | null
          date_range_end?: string | null
          account_type?: 'general' | 'reserve' | null
          record_count?: number
          exported_by?: string
          created_at?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      user_role: 'admin' | 'member' | 'viewer'
      account_type: 'general' | 'reserve'
      transaction_type: 'income' | 'expense'
      plan_type: 'budget' | 'simulation'
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}